<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import HandoffConditionsList from './HandoffConditionsList.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const getTeamMembers = useMapGetter('teamMembers/getTeamMembers');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

const HANDOFF_TEAM_UNSET = '';
const handoffTeamOptions = computed(() => [
  {
    value: HANDOFF_TEAM_UNSET,
    label: t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.INBOX_QUEUE'),
  },
  ...teams.value.map(team => ({ value: String(team.id), label: team.name })),
]);

const DISTRIBUTION_MODES = ['fixed_user', 'roulette'];

// The team scopes who can be picked: with a team chosen, only its members are
// offered for the fixed user and the roulette.
const loadedTeamIds = ref([]);
const removedFromSelection = ref(0);

const state = reactive({
  handoffTeamId: HANDOFF_TEAM_UNSET,
  conditions: [],
  distributionMode: 'fixed_user',
  fixedUserId: '',
  rouletteTargets: [],
  summaryEnabled: false,
});

const selectedTeam = computed(() =>
  teams.value.find(team => String(team.id) === state.handoffTeamId)
);

const scopedMemberIds = computed(() => {
  if (
    !state.handoffTeamId ||
    !loadedTeamIds.value.includes(state.handoffTeamId)
  ) {
    return null;
  }
  return getTeamMembers
    .value(state.handoffTeamId)
    .map(member => String(member.id));
});

const isOutsideTeam = userId =>
  !!userId &&
  !!scopedMemberIds.value &&
  !scopedMemberIds.value.includes(userId);

const selectedUserIds = () =>
  [
    state.distributionMode === 'fixed_user' ? state.fixedUserId : null,
    ...(state.distributionMode === 'roulette'
      ? state.rouletteTargets.map(target => target.userId)
      : []),
  ].filter(Boolean);

const agentOptions = computed(() =>
  agents.value
    .filter(
      agent =>
        !scopedMemberIds.value ||
        scopedMemberIds.value.includes(String(agent.id)) ||
        selectedUserIds().includes(String(agent.id))
    )
    .map(agent => ({
      value: String(agent.id),
      label: isOutsideTeam(String(agent.id))
        ? `${agent.name} (${t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.OUT_OF_TEAM')})`
        : agent.name,
    }))
);

const outsideTeamCount = computed(
  () => selectedUserIds().filter(isOutsideTeam).length
);

const hasPersonSelected = computed(() => selectedUserIds().length > 0);

const showNoAutoAssignHint = computed(
  () =>
    !!selectedTeam.value &&
    !selectedTeam.value.allow_auto_assign &&
    !hasPersonSelected.value
);

const loadTeamMembers = async teamId => {
  if (!teamId || loadedTeamIds.value.includes(teamId)) return;
  try {
    await store.dispatch('teamMembers/get', { teamId: Number(teamId) });
    loadedTeamIds.value.push(teamId);
  } catch {
    // Without the member list the selects fall back to every agent.
  }
};

const pruneSelectionToTeam = () => {
  let removed = 0;
  if (isOutsideTeam(state.fixedUserId)) {
    state.fixedUserId = '';
    removed += 1;
  }
  state.rouletteTargets.forEach(target => {
    if (isOutsideTeam(target.userId)) {
      // eslint-disable-next-line no-param-reassign
      target.userId = '';
      removed += 1;
    }
  });
  removedFromSelection.value = removed;
};

const handleTeamChange = async teamId => {
  state.handoffTeamId = teamId;
  removedFromSelection.value = 0;
  await loadTeamMembers(teamId);
  if (state.handoffTeamId === teamId) pruneSelectionToTeam();
};

// A roulette person without a selected user would be silently dropped on save,
// so the page asks the form to validate before saving.
const showErrors = ref(false);
const isMissingUser = target =>
  state.distributionMode === 'roulette' && !target.userId;
const validate = () => {
  showErrors.value = true;
  return !state.rouletteTargets.some(isMissingUser);
};

// Stable per-person id for the v-for key — an index key would mix up the
// people (and their condition lists) when one of them is removed.
let nextTargetId = 0;
const generateTargetId = () => {
  nextTargetId += 1;
  return nextTargetId;
};

const addRouletteTarget = () => {
  state.rouletteTargets.push({
    id: generateTargetId(),
    userId: '',
    conditions: [],
  });
};

const removeRouletteTarget = index => {
  state.rouletteTargets.splice(index, 1);
};

const serializeConditions = conditions =>
  conditions.map(({ text, enabled }) => ({ text, enabled }));

// Both modes are always sent, so switching the active mode never erases what
// was configured for the other one.
const serializeDistribution = () => ({
  mode: state.distributionMode,
  fixed_user_id: state.fixedUserId || null,
  roulette_targets: state.rouletteTargets
    .filter(target => target.userId)
    .map(target => ({
      user_id: target.userId,
      conditions: serializeConditions(target.conditions),
    })),
});

// Returns this section's partial payload only — never spreads
// props.assistant.config, so the parent page can merge all sections'
// payloads together in one PATCH instead of each section overwriting the
// others' unsaved changes with a stale snapshot of assistant.config.
const getPayload = () => ({
  config: {
    handoff_team_id: state.handoffTeamId || null,
    handoff_conditions: serializeConditions(state.conditions),
    handoff_distribution: serializeDistribution(),
    handoff_summary_enabled: state.summaryEnabled,
  },
});

// Compares serialized payloads (not raw state) so the generated per-person
// `id` — which changes every time updateStateFromAssistant runs but is never
// part of what gets saved — doesn't cause a false positive dirty state.
let initialPayloadSnapshot = JSON.stringify(getPayload());

const isDirty = computed(
  () => JSON.stringify(getPayload()) !== initialPayloadSnapshot
);

const hydrateConditions = conditions =>
  (conditions || []).map(condition => ({
    text: condition.text || '',
    enabled: condition.enabled !== false,
  }));

// Older roulette targets stored a single "when_to_transfer" text (or an even
// older "specialties" list) instead of a list of conditions: show it as one.
const hydrateTargetConditions = target => {
  if (Array.isArray(target.conditions)) {
    return hydrateConditions(target.conditions);
  }
  const legacyText =
    target.when_to_transfer || Array(target.specialties).join(', ');
  return legacyText ? [{ text: legacyText, enabled: true }] : [];
};

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.handoffTeamId = config.handoff_team_id
    ? String(config.handoff_team_id)
    : HANDOFF_TEAM_UNSET;
  state.conditions = hydrateConditions(config.handoff_conditions);

  const distribution = config.handoff_distribution || {};
  state.distributionMode = DISTRIBUTION_MODES.includes(distribution.mode)
    ? distribution.mode
    : 'fixed_user';
  state.fixedUserId = distribution.fixed_user_id
    ? String(distribution.fixed_user_id)
    : '';
  state.rouletteTargets = (distribution.roulette_targets || []).map(target => ({
    id: generateTargetId(),
    userId: target.user_id ? String(target.user_id) : '',
    conditions: hydrateTargetConditions(target),
  }));

  state.summaryEnabled = config.handoff_summary_enabled || false;
  showErrors.value = false;
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

defineExpose({ getPayload, isDirty, validate });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);

// Saved team (page load): fetch its members so the lists are scoped.
watch(() => state.handoffTeamId, loadTeamMembers, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.LABEL') }}
      </label>
      <Select
        :model-value="state.handoffTeamId"
        :options="handoffTeamOptions"
        @update:model-value="handleTeamChange"
      />
      <p v-if="selectedTeam" class="text-xs text-n-slate-11">
        {{
          t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.MEMBERS_ONLY', {
            team: selectedTeam.name,
          })
        }}
      </p>
      <p v-if="removedFromSelection" class="text-xs text-n-amber-11">
        {{
          t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.REMOVED_NOTICE', {
            count: removedFromSelection,
          })
        }}
      </p>
      <p v-if="outsideTeamCount" class="text-xs text-n-ruby-11">
        {{
          t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.OUTSIDE_WARNING', {
            count: outsideTeamCount,
          })
        }}
      </p>
      <p v-if="showNoAutoAssignHint" class="text-xs text-n-amber-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_TEAM.NO_AUTO_ASSIGN') }}
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.LABEL') }}
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg transition-colors duration-200"
          :class="
            state.distributionMode === 'fixed_user'
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-black2'
          "
          @click="state.distributionMode = 'fixed_user'"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.FIXED_USER.LABEL'
              )
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.FIXED_USER.DESCRIPTION'
              )
            }}
          </span>
        </button>
        <button
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg transition-colors duration-200"
          :class="
            state.distributionMode === 'roulette'
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-black2'
          "
          @click="state.distributionMode = 'roulette'"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.ROULETTE.LABEL'
              )
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.ROULETTE.DESCRIPTION'
              )
            }}
          </span>
        </button>
      </div>

      <div
        v-if="state.distributionMode === 'fixed_user'"
        class="flex flex-col gap-2"
      >
        <label class="text-sm text-n-slate-12">
          {{
            t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.SELECT_USER')
          }}
        </label>
        <Select
          v-model="state.fixedUserId"
          :options="agentOptions"
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.SELECT_USER_PLACEHOLDER'
            )
          "
        />
        <HandoffConditionsList
          v-model="state.conditions"
          class="mt-2"
          :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.CONDITIONS.LABEL')"
        />
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="(target, index) in state.rouletteTargets"
          :key="target.id"
          class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg"
        >
          <div class="flex items-center gap-2">
            <Select
              v-model="target.userId"
              class="flex-1"
              :options="agentOptions"
              :error="showErrors && isMissingUser(target) ? 'required' : ''"
              :placeholder="
                t(
                  'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.SELECT_USER_PLACEHOLDER'
                )
              "
            />
            <Button
              icon="i-lucide-trash"
              variant="ghost"
              color="slate"
              size="sm"
              @click="removeRouletteTarget(index)"
            />
          </div>
          <span
            v-if="showErrors && isMissingUser(target)"
            class="text-xs text-n-ruby-11"
          >
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.SELECT_USER_REQUIRED'
              )
            }}
          </span>
          <HandoffConditionsList
            v-model="target.conditions"
            :label="
              t(
                'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.WHEN_TO_TRANSFER_LABEL'
              )
            "
          />
        </div>

        <Button
          icon="i-lucide-plus"
          variant="ghost"
          size="sm"
          class="!w-fit !text-n-brand hover:!text-n-brand"
          :label="
            t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.ADD_USER')
          "
          @click="addRouletteTarget"
        />
      </div>
    </div>

    <div class="flex items-center gap-3">
      <Switch v-model="state.summaryEnabled" />
      <div class="flex flex-col">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.SUMMARY.LABEL') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.SUMMARY.DESCRIPTION') }}
        </span>
      </div>
    </div>
  </div>
</template>
